import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { it, enUS } from 'date-fns/locale';
import { UserRelationshipType } from '@timesheet/schemas';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Download,
  Filter,
  Loader2,
  Mail,
  Search,
  X,
} from 'lucide-react';
import { cn, capitalize } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { PageTransition } from '../components/PageTransition';
import { ApprovalScopeToggle } from '../components/approvals/ApprovalScopeToggle';
import { useApprovalScope } from '../hooks/use-approval-scope';
import { EmptyState } from '../components/shared/empty-state';
import { MonthStrip } from '../components/approvals/MonthStrip';
import { TimesheetMonthDialog } from '../components/approvals/TimesheetMonthDialog';
import { useApprovalDecisions } from '../components/approvals/useApprovalDecisions';
import { IssueBadges } from '../components/approvals/TimesheetIssues';
import { MonthCalendarLine } from '../components/approvals/TimesheetMonthDetail';
import {
  DayCell,
  PersonMonthRow,
  RELATIONSHIP_TABS,
  buildMonthCells,
  buildRows,
  formatHours,
  groupRowsByType,
  isRelationshipType,
  issueDayKeys,
  monthKey,
  offDaysByKey,
  parseMonthKey,
  sumRows,
} from '../components/approvals/approvals.utils';
import { useNotification } from '../contexts/NotificationContext';
import { timesheetService, MonthCalendar, PendingApproval, type ManagementFilters } from '../services/timesheetService';
import { timesheetReminderService, type ReminderStatus } from '../services/timesheetReminderService';
import { userService } from '../services/userService';
import { orderService } from '../services/orderService';
import { clientSupplierService } from '../services/clientSupplierService';


const DEFAULT_TAB: UserRelationshipType = 'INTERNAL_EMPLOYEE';

/** Column header: small caps, like the head of a ledger. */
const headClass = 'h-9 whitespace-nowrap px-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground';

const ApprovalsManager: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith('it') ? it : enUS;
  const numberLocale = i18n.language || 'en';

  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [searchParams, setSearchParams] = useSearchParams();

  // Month and tab live in the URL so a review can be shared or resumed.
  const today = new Date();
  const { year, month } = parseMonthKey(searchParams.get('month')) ?? {
    year: today.getFullYear(),
    month: today.getMonth() + 1,
  };
  const tabParam = searchParams.get('tab');
  const activeTab: UserRelationshipType = isRelationshipType(tabParam) ? tabParam : DEFAULT_TAB;
  const currentMonthKey = monthKey(today.getFullYear(), today.getMonth() + 1);
  const selectedMonthKey = monthKey(year, month);
  const monthLabel = capitalize(format(new Date(year, month - 1, 1), 'LLLL yyyy', { locale: dateLocale }));

  const updateParams = useCallback(
    (patch: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(patch).forEach(([key, value]) => {
            if (value === null) next.delete(key);
            else next.set(key, value);
          });
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const goToMonth = (offset: number) => {
    const target = new Date(year, month - 1 + offset, 1);
    updateParams({ month: monthKey(target.getFullYear(), target.getMonth() + 1) });
  };

  const [groups, setGroups] = useState<PendingApproval[]>([]);
  const [loading, setLoading] = useState(true);
  // One selection for the whole page: people, not entries. Approve/reject act on the entries of
  // the selected people, "Invia promemoria" on the people themselves.
  const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(new Set());
  const [detailUserId, setDetailUserId] = useState<number | null>(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [reminderStatus, setReminderStatus] = useState<ReminderStatus | null>(null);
  const [calendar, setCalendar] = useState<MonthCalendar | null>(null);

  // Filters
  const [users, setUsers] = useState<Array<{ id: number; name: string; supplierId?: number | null; relationshipType?: UserRelationshipType }>>([]);
  const [orders, setOrders] = useState<Array<{ id: number; code: string; clientId: number }>>([]);
  const [companies, setCompanies] = useState<Array<{ id: number; name: string }>>([]);
  const [search, setSearch] = useState('');
  const [filterOrderId, setFilterOrderId] = useState<string>('all');
  const [filterUserCompanyId, setFilterUserCompanyId] = useState<string>('all');
  const [filterClientCompanyId, setFilterClientCompanyId] = useState<string>('all');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  // "My approvals" vs everybody: a scope, not a filter, so "Azzera filtri" leaves it alone
  const scope = useApprovalScope();

  const fetchFiltersData = useCallback(async () => {
    try {
      const [usersData, ordersData, companiesData] = await Promise.all([
        userService.getAll(),
        orderService.getAll(),
        clientSupplierService.getAll(),
      ]);
      setUsers(usersData.map((u) => ({ id: u.id, name: u.name, supplierId: u.supplierId, relationshipType: u.relationshipType })));
      setOrders(ordersData.map((o) => ({ id: o.id, code: o.code, clientId: o.clientId })));
      setCompanies(companiesData.map((c) => ({ id: c.id, name: c.name })));
    } catch (error) {
      console.error('Failed to fetch filter data:', error);
    }
  }, []);

  const companyNameById = useMemo(() => new Map(companies.map((company) => [company.id, company.name])), [companies]);
  const usersById = useMemo(() => new Map(users.map((user) => [user.id, user])), [users]);

  const userCompanyOptions = useMemo(() => {
    const uniqueIds = new Set<number>();
    users.forEach((u) => {
      const supplierId = Number(u.supplierId);
      if (supplierId > 0) uniqueIds.add(supplierId);
    });
    return Array.from(uniqueIds)
      .map((id) => ({ id, name: companyNameById.get(id) || `#${id}` }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [companyNameById, users]);

  const clientCompanyOptions = useMemo(() => {
    const uniqueIds = new Set<number>();
    orders.forEach((order) => {
      const clientId = Number(order.clientId);
      if (clientId > 0) uniqueIds.add(clientId);
    });
    return Array.from(uniqueIds)
      .map((id) => ({ id, name: companyNameById.get(id) || `#${id}` }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [companyNameById, orders]);

  const buildFilters = useCallback((): ManagementFilters => {
    const filters: ManagementFilters = {};
    if (filterOrderId !== 'all') filters.orderId = parseInt(filterOrderId, 10);
    if (filterUserCompanyId !== 'all') filters.userCompanyId = parseInt(filterUserCompanyId, 10);
    if (filterClientCompanyId !== 'all') filters.clientCompanyId = parseInt(filterClientCompanyId, 10);
    if (scope.onlyMine) filters.onlyMine = true;
    return filters;
  }, [filterOrderId, filterUserCompanyId, filterClientCompanyId, scope.onlyMine]);

  const hasActiveFilters = filterOrderId !== 'all' || filterUserCompanyId !== 'all' || filterClientCompanyId !== 'all' || search.trim() !== '';

  const resetFilters = () => {
    setSearch('');
    setFilterOrderId('all');
    setFilterUserCompanyId('all');
    setFilterClientCompanyId('all');
  };

  const fetchData = useCallback(async () => {
    // Wait for the scope so the month is fetched once, already narrowed to "my approvals" when that is the default
    if (!scope.ready) return;
    setLoading(true);
    try {
      // includeWithoutEntries: who has not submitted is exactly who the desk has to chase
      const data = await timesheetService.getManagementTimesheets(year, month, { ...buildFilters(), includeWithoutEntries: true });
      setGroups(
        data.map((group) => ({
          ...group,
          stats: {
            workedDays: Number(group?.stats?.workedDays) || 0,
            vacationDays: Number(group?.stats?.vacationDays) || 0,
            onCallDays: Number(group?.stats?.onCallDays) || 0,
            permissionDays: Number(group?.stats?.permissionDays) || 0,
            sicknessDays: Number(group?.stats?.sicknessDays) || 0,
            rolDays: Number(group?.stats?.rolDays) || 0,
          },
        })),
      );
    } catch (error) {
      console.error('Failed to fetch timesheets:', error);
      showNotification(t('approvals.errors.fetch', 'Failed to load timesheets'), 'error');
    } finally {
      setLoading(false);
    }
  }, [year, month, buildFilters, scope.ready, showNotification, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchFiltersData();
  }, [fetchFiltersData]);

  // Who already has a live link for this month, so a row can say it and a second send is a choice
  const fetchReminderStatus = useCallback(async () => {
    try {
      setReminderStatus(await timesheetReminderService.getStatus(year, month));
    } catch (error) {
      console.error('Failed to fetch reminder status:', error);
      setReminderStatus(null);
    }
  }, [year, month]);

  useEffect(() => {
    fetchReminderStatus();
  }, [fetchReminderStatus]);

  // The month's yardstick (working days, expected hours, off days) depends on the month only, not on the filters
  useEffect(() => {
    let cancelled = false;
    setCalendar(null);
    timesheetService
      .getManagementMonthCalendar(year, month)
      .then((data) => {
        if (!cancelled) setCalendar(data);
      })
      .catch((error) => console.error('Failed to fetch month calendar:', error));
    return () => {
      cancelled = true;
    };
  }, [year, month]);

  // Rows and tabs
  const rows = useMemo(
    () => buildRows(groups, (userId) => usersById.get(userId)?.relationshipType),
    [groups, usersById],
  );

  const visibleRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return query ? rows.filter((row) => row.name.toLowerCase().includes(query)) : rows;
  }, [rows, search]);

  const rowsByType = useMemo(() => groupRowsByType(visibleRows), [visibleRows]);
  const tabRows = rowsByType[activeTab];
  const monthTotals = useMemo(() => sumRows(rows), [rows]);
  const notSubmittedCount = useMemo(() => rows.filter((row) => row.entries.length === 0).length, [rows]);
  const monthHours = rows.reduce((sum, row) => sum + row.workedHours + row.overtimeHours + row.onCallHours, 0);

  const cellsByUser = useMemo(() => {
    const map = new Map<number, DayCell[]>();
    rows.forEach((row) => map.set(row.userId, buildMonthCells(row.entries, year, month)));
    return map;
  }, [rows, year, month]);

  // Holidays and company closings of the month, blank in the strips like weekends
  const offDays = useMemo(() => offDaysByKey(calendar), [calendar]);

  // Selection (people, shared across tabs so one action can span relationship types)
  const tabUserIds = useMemo(() => tabRows.map((row) => row.userId), [tabRows]);
  const allTabSelected = tabUserIds.length > 0 && tabUserIds.every((id) => selectedUserIds.has(id));
  const someTabSelected = !allTabSelected && tabUserIds.some((id) => selectedUserIds.has(id));
  const selectedRows = useMemo(() => rows.filter((row) => selectedUserIds.has(row.userId)), [rows, selectedUserIds]);
  const selectedPendingIds = useMemo(() => selectedRows.flatMap((row) => row.pendingIds), [selectedRows]);
  const reminderByUser = useMemo(
    () => new Map((reminderStatus?.recipients ?? []).map((recipient) => [recipient.userId, recipient])),
    [reminderStatus],
  );
  const emailDisabled = reminderStatus !== null && !reminderStatus.emailEnabled;

  const toggleTabSelection = () => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      tabUserIds.forEach((id) => (allTabSelected ? next.delete(id) : next.add(id)));
      return next;
    });
  };

  const toggleRowSelection = (row: PersonMonthRow) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(row.userId)) next.delete(row.userId);
      else next.add(row.userId);
      return next;
    });
  };

  /** After a decision only the people whose entries failed stay selected, ready for a retry. */
  const settleSelection = (_ids: number[], failed: { id: number }[]) => {
    const failedIds = new Set(failed.map((entry) => entry.id));
    setSelectedUserIds((prev) => {
      const next = new Set<number>();
      rows.forEach((row) => {
        if (prev.has(row.userId) && row.pendingIds.some((id) => failedIds.has(id))) next.add(row.userId);
      });
      return next;
    });
  };

  // Labels
  const peopleLabel = (count: number) =>
    count === 1 ? t('approvals.count.personOne', '1 persona') : t('approvals.count.personOther', '{{count}} persone', { count });
  const entriesLabel = (count: number) =>
    count === 1 ? t('approvals.count.entryOne', '1 voce') : t('approvals.count.entryOther', '{{count}} voci', { count });
  const pendingLabel = (count: number) =>
    count === 1 ? t('approvals.count.pendingOne', '1 voce in attesa') : t('approvals.count.pendingOther', '{{count}} voci in attesa', { count });
  const relationshipLabel = (type: UserRelationshipType) => t(`pages.usersManager.relationshipTypes.${type}`, type);
  const companyLabelFor = (row: PersonMonthRow) => {
    const supplierId = Number(usersById.get(row.userId)?.supplierId);
    return supplierId > 0 ? companyNameById.get(supplierId) : undefined;
  };

  const stripCellTitle = (cell: DayCell) => {
    const day = capitalize(format(parseISO(cell.dateKey), 'EEE d MMM', { locale: dateLocale }));
    if (cell.entries === 0) {
      const offDay = offDays.get(cell.dateKey);
      const reason =
        offDay === 'holiday'
          ? t('approvals.strip.holiday', 'Festività')
          : offDay === 'closing'
            ? t('approvals.strip.closing', 'Chiusura aziendale')
            : cell.isWeekend
              ? t('approvals.strip.weekend', 'Weekend')
              : t('approvals.strip.noEntry', 'Nessuna voce');
      return `${day} · ${reason}`;
    }
    const parts = [t('approvals.strip.worked', '{{hours}} h lavorate', { hours: formatHours(cell.workedHours, numberLocale) })];
    if (cell.overtimeHours > 0) parts.push(t('approvals.strip.overtime', '{{hours}} h straordinario', { hours: formatHours(cell.overtimeHours, numberLocale) }));
    if (cell.onCall) parts.push(t('approvals.strip.onCall', '{{hours}} h reperibilità', { hours: formatHours(cell.onCallHours, numberLocale) }));
    if (cell.permitHours > 0) parts.push(t('approvals.strip.permit', '{{hours}} h assenze', { hours: formatHours(cell.permitHours, numberLocale) }));
    const state = cell.state === 'approved' ? t('timesheet.states.approved', 'Approvato') : t('timesheet.states.in_approval', 'In approvazione');
    return `${day} · ${parts.join(' · ')} · ${state}`;
  };

  // Decisions (approve / reject, with the expense-claims confirmation) are shared with the person page.
  const { processing, beginApprove, beginReject, dialogs: decisionDialogs } = useApprovalDecisions({
    rows,
    monthKey: selectedMonthKey,
    usersById,
    refetch: fetchData,
    onSettled: settleSelection,
  });

  const handleDownloadManagementPdf = async () => {
    try {
      setDownloadingPdf(true);
      // Exactly what the desk shows: the list's query plus the name search it applies in the browser
      const blob = await timesheetService.downloadManagementPdf(year, month, {
        ...buildFilters(),
        includeWithoutEntries: true,
        search: search.trim() || undefined,
      });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `timesheet-management-${selectedMonthKey}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download management PDF:', error);
      showNotification(t('approvals.errors.downloadPdf', 'Failed to download PDF'), 'error');
    } finally {
      setDownloadingPdf(false);
    }
  };

  /**
   * Sends the monthly reminder straight to the selected people: no confirmation step, the button
   * carries the count and the toast reports who actually received it.
   */
  const handleSendReminders = async () => {
    const userIds = Array.from(selectedUserIds);
    if (userIds.length === 0 || sendingReminders) return;

    setSendingReminders(true);
    try {
      const result = await timesheetReminderService.send(year, month, userIds);
      if (result.failed.length === 0) {
        showNotification(
          result.sent.length === 1
            ? t('approvals.reminder.sentOne', 'Promemoria inviato a 1 persona')
            : t('approvals.reminder.sent', 'Promemoria inviato a {{count}} persone', { count: result.sent.length }),
          'success',
        );
        setSelectedUserIds(new Set());
      } else {
        showNotification(
          t('approvals.reminder.partial', '{{sent}} inviati, {{failed}} non riusciti', {
            sent: result.sent.length,
            failed: result.failed.length,
          }),
          'warning',
        );
        // Keep the ones that failed selected, so a retry is one click away
        setSelectedUserIds(new Set(result.failed.map((failure) => failure.userId)));
      }
      await fetchReminderStatus();
    } catch (error) {
      console.error('Failed to send timesheet reminders:', error);
      showNotification(t('approvals.reminder.errors.send', 'Impossibile inviare i promemoria'), 'error');
    } finally {
      setSendingReminders(false);
    }
  };

  const handleOpenFullTimesheet = (userId: number) => {
    navigate(`/approvals/${userId}?month=${selectedMonthKey}`);
  };

  // Detail popup follows the live row, and closes if the person drops out of the month.
  const detailRow = useMemo(() => rows.find((row) => row.userId === detailUserId) ?? null, [rows, detailUserId]);
  useEffect(() => {
    if (detailUserId !== null && !loading && !detailRow) setDetailUserId(null);
  }, [detailUserId, loading, detailRow]);

  const renderFilterSelects = (triggerClass: string) => (
    <>
      <Select value={filterOrderId} onValueChange={setFilterOrderId}>
        <SelectTrigger className={triggerClass} aria-label={t('approvals.filters.allOrders', 'All Orders')}>
          <SelectValue placeholder={t('approvals.filters.allOrders', 'All Orders')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('approvals.filters.allOrders', 'All Orders')}</SelectItem>
          {orders.map((order) => (
            <SelectItem key={order.id} value={String(order.id)}>
              {order.code}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={filterUserCompanyId} onValueChange={setFilterUserCompanyId}>
        <SelectTrigger className={triggerClass} aria-label={t('approvals.filters.userCompany', 'Azienda utente')}>
          <SelectValue placeholder={t('approvals.filters.userCompany', 'Azienda utente')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('approvals.filters.allUserCompanies', 'Tutte le aziende utente')}</SelectItem>
          {userCompanyOptions.map((company) => (
            <SelectItem key={company.id} value={String(company.id)}>
              {company.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={filterClientCompanyId} onValueChange={setFilterClientCompanyId}>
        <SelectTrigger className={triggerClass} aria-label={t('approvals.filters.clientCompany', 'Azienda cliente')}>
          <SelectValue placeholder={t('approvals.filters.clientCompany', 'Azienda cliente')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('approvals.filters.allClientCompanies', 'Tutte le aziende cliente')}</SelectItem>
          {clientCompanyOptions.map((company) => (
            <SelectItem key={company.id} value={String(company.id)}>
              {company.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </>
  );

  const renderRow = (row: PersonMonthRow) => {
    const pendingCount = row.pendingIds.length;
    const isSelected = selectedUserIds.has(row.userId);
    // Nothing in `in_approval` or `approved`: either untouched or still a draft, either way not submitted
    const notSubmitted = row.entries.length === 0;
    const reminderLink = reminderByUser.get(row.userId)?.link ?? null;
    const company = companyLabelFor(row);
    const cells = cellsByUser.get(row.userId) ?? [];
    const daysWithEntries = cells.filter((cell) => cell.entries > 0).length;
    const issueDays = issueDayKeys(row.issues);

    return (
      <TableRow
        key={row.userId}
        className={cn('cursor-pointer', isSelected && 'bg-amber-50/70 hover:bg-amber-50 dark:bg-amber-950/20 dark:hover:bg-amber-950/30')}
        onClick={() => setDetailUserId(row.userId)}
      >
        <TableCell className="pl-4 pr-0" onClick={(event) => event.stopPropagation()}>
          <Checkbox aria-label={row.name} checked={isSelected} onCheckedChange={() => toggleRowSelection(row)} />
        </TableCell>
        <TableCell className="px-3 py-2.5">
          <button
            type="button"
            className="text-left font-medium leading-tight hover:underline focus-visible:underline focus-visible:outline-none"
            onClick={(event) => {
              event.stopPropagation();
              setDetailUserId(row.userId);
            }}
          >
            {row.name}
          </button>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
            {notSubmitted ? (
              <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50" />
                {t('approvals.rowStatus.notSubmitted', 'Non inviato')}
              </span>
            ) : pendingCount > 0 ? (
              <span className="inline-flex items-center gap-1.5 text-amber-700 dark:text-amber-400">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                {t('approvals.rowStatus.pending', '{{entries}} da approvare', { entries: entriesLabel(pendingCount) })}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-400">
                <Check className="h-3 w-3" />
                {t('approvals.rowStatus.approved', 'Approvato')}
              </span>
            )}
            {reminderLink && (
              <span
                className="text-muted-foreground"
                title={
                  reminderLink.useCount === 1
                    ? t('approvals.reminder.rowOpenedOne', 'Link aperto 1 volta')
                    : reminderLink.useCount > 1
                      ? t('approvals.reminder.rowOpened', 'Link aperto {{count}} volte', { count: reminderLink.useCount })
                      : t('approvals.reminder.rowNeverOpened', 'Link non ancora aperto')
                }
              >
                · {t('approvals.reminder.rowSent', 'Promemoria {{date}}', { date: format(parseISO(reminderLink.sentAt), 'd MMM', { locale: dateLocale }) })}
              </span>
            )}
            {company && <span className="text-muted-foreground">· {company}</span>}
          </div>
          {/* Phone: the hidden columns fold under the name */}
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground md:hidden">
            {row.overtimeHours > 0 && (
              <span className="tabular-nums">{t('approvals.columns.overtimeHours', 'Straordinari')} {formatHours(row.overtimeHours, numberLocale)}</span>
            )}
            {row.onCallHours > 0 && (
              <span className="tabular-nums">{t('approvals.columns.onCallHours', 'Reperibilità')} {formatHours(row.onCallHours, numberLocale)}</span>
            )}
            {row.issues.length > 0 && <IssueBadges issues={row.issues} />}
          </div>
        </TableCell>
        <TableCell className="hidden px-3 lg:table-cell">
          <MonthStrip
            cells={cells}
            isOffDay={(dateKey) => offDays.has(dateKey)}
            isIssueDay={(dateKey) => issueDays.has(dateKey)}
            cellTitle={stripCellTitle}
            label={t('approvals.strip.label', '{{name}}: {{days}} giorni con voci, {{pending}} da approvare', {
              name: row.name,
              days: daysWithEntries,
              pending: pendingCount,
            })}
          />
        </TableCell>
        <TableCell className={cn('px-3 text-right tabular-nums', row.workedHours === 0 && 'text-muted-foreground/50')}>
          {formatHours(row.workedHours, numberLocale)}
        </TableCell>
        <TableCell className={cn('hidden px-3 text-right tabular-nums md:table-cell', row.overtimeHours === 0 && 'text-muted-foreground/50')}>
          {formatHours(row.overtimeHours, numberLocale)}
        </TableCell>
        <TableCell className={cn('hidden px-3 text-right tabular-nums md:table-cell', row.onCallHours === 0 && 'text-muted-foreground/50')}>
          {formatHours(row.onCallHours, numberLocale)}
        </TableCell>
        <TableCell className="hidden max-w-[320px] px-3 py-2 md:table-cell">
          <IssueBadges issues={row.issues} />
        </TableCell>
        <TableCell className="hidden pl-0 pr-3 text-right sm:table-cell">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground"
            aria-label={`${t('approvals.openDetail', 'Apri dettaglio')} · ${row.name}`}
            onClick={(event) => {
              event.stopPropagation();
              setDetailUserId(row.userId);
            }}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </TableCell>
      </TableRow>
    );
  };

  const renderTable = (type: UserRelationshipType) => {
    const list = rowsByType[type];

    if (loading) {
      return (
        <div className="overflow-hidden rounded-lg border bg-card">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="flex items-center gap-4 border-b px-4 py-3 last:border-b-0">
              <Skeleton className="h-4 w-4" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-3 w-24" />
              </div>
              <Skeleton className="hidden h-4 w-44 lg:block" />
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-4 w-12" />
            </div>
          ))}
        </div>
      );
    }

    if (list.length === 0) {
      return (
        <div className="rounded-lg border border-dashed bg-card">
          <EmptyState
            icon={ClipboardCheck}
            title={t('approvals.emptyTab.title', 'Nessuna persona · {{tab}}', { tab: t(`approvals.tabs.${type}`, type) })}
            description={t('approvals.emptyTab.description', 'Nessuna persona di questa tipologia a {{month}}. Cambia mese o azzera i filtri.', { month: monthLabel })}
            action={hasActiveFilters ? { label: t('approvals.filters.reset', 'Azzera filtri'), onClick: resetFilters, icon: X } : undefined}
          />
        </div>
      );
    }

    const totals = sumRows(list);

    return (
      <div className="overflow-hidden rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className={cn(headClass, 'w-10 pl-4 pr-0')}>
                <Checkbox
                  aria-label={t('approvals.selectAll', 'Seleziona tutto')}
                  checked={allTabSelected ? true : someTabSelected ? 'indeterminate' : false}
                  disabled={tabUserIds.length === 0}
                  onCheckedChange={toggleTabSelection}
                />
              </TableHead>
              <TableHead className={cn(headClass, 'md:min-w-[200px]')}>{t('approvals.columns.person', 'Persona')}</TableHead>
              <TableHead className={cn(headClass, 'hidden lg:table-cell')}>{t('approvals.columns.month', 'Mese')}</TableHead>
              <TableHead className={cn(headClass, 'h-auto whitespace-nowrap py-2 text-right align-bottom')}>
                {t('approvals.columns.workedHours', 'Ore lavorate')}
                {calendar && (
                  <span className="block text-[10px] font-medium normal-case tracking-normal text-muted-foreground/80">
                    {t('approvals.columns.expectedShort', 'attese {{hours}}', { hours: formatHours(calendar.expectedHours, numberLocale) })}
                  </span>
                )}
              </TableHead>
              <TableHead className={cn(headClass, 'hidden text-right md:table-cell')}>{t('approvals.columns.overtimeHours', 'Straordinari')}</TableHead>
              <TableHead className={cn(headClass, 'hidden text-right md:table-cell')}>{t('approvals.columns.onCallHours', 'Reperibilità')}</TableHead>
              <TableHead className={cn(headClass, 'hidden md:table-cell')}>{t('approvals.columns.issues', 'Problemi')}</TableHead>
              <TableHead className={cn(headClass, 'hidden w-12 sm:table-cell')} />
            </TableRow>
          </TableHeader>
          <TableBody>{list.map(renderRow)}</TableBody>
          <TableFooter className="bg-muted/40">
            <TableRow className="hover:bg-transparent">
              <TableCell className="pl-4 pr-0" />
              <TableCell className="px-3 text-sm">
                <span className="font-semibold">{t('approvals.columns.total', 'Totale')}</span>
                <span className="text-muted-foreground"> · {peopleLabel(totals.people)}</span>
              </TableCell>
              <TableCell className="hidden px-3 lg:table-cell" />
              <TableCell className="px-3 text-right font-semibold tabular-nums">{formatHours(totals.workedHours, numberLocale)}</TableCell>
              <TableCell className="hidden px-3 text-right font-semibold tabular-nums md:table-cell">{formatHours(totals.overtimeHours, numberLocale)}</TableCell>
              <TableCell className="hidden px-3 text-right font-semibold tabular-nums md:table-cell">{formatHours(totals.onCallHours, numberLocale)}</TableCell>
              <TableCell className="hidden px-3 md:table-cell" />
              <TableCell className="hidden pl-0 pr-3 sm:table-cell" />
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    );
  };

  return (
    <PageTransition>
      <div className="space-y-5 p-4 sm:p-6">
        {/* Header: the month is the unit of work, so the month is the headline */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="space-y-1">
            <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-orange-600 dark:text-orange-400">
              <ClipboardCheck className="h-3.5 w-3.5" />
              {t('approvals.title', 'Gestione timesheet')}
            </p>
            <div className="flex flex-wrap items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label={t('approvals.month.previous', 'Mese precedente')}
                onClick={() => goToMonth(-1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <h1 className="min-w-[10ch] text-center text-3xl font-semibold tracking-tight sm:text-left">{monthLabel}</h1>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label={t('approvals.month.next', 'Mese successivo')}
                onClick={() => goToMonth(1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              {selectedMonthKey !== currentMonthKey && (
                <Button type="button" variant="link" size="sm" className="h-8 px-2 text-muted-foreground" onClick={() => updateParams({ month: null })}>
                  {t('approvals.month.current', 'Vai al mese corrente')}
                </Button>
              )}
            </div>
            <MonthCalendarLine calendar={calendar} />
            <p className="text-sm tabular-nums text-muted-foreground">
              {loading
                ? '…'
                : [
                    peopleLabel(monthTotals.people),
                    ...(notSubmittedCount > 0
                      ? [t('approvals.count.notSubmitted', '{{count}} non inviati', { count: notSubmittedCount })]
                      : []),
                    pendingLabel(monthTotals.pendingCount),
                    t('approvals.count.hoursTotal', '{{hours}} h totali', { hours: formatHours(monthHours, numberLocale) }),
                    ...(monthTotals.issuesCount > 0 ? [t('approvals.issues.tabCount', '{{count}} con problemi', { count: monthTotals.issuesCount })] : []),
                  ].join(' · ')}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" className="gap-2 md:hidden" onClick={() => setMobileFiltersOpen(true)}>
              <Filter className="h-4 w-4" />
              {t('approvals.filters.open', 'Filtri')}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={handleSendReminders}
              disabled={selectedUserIds.size === 0 || sendingReminders || emailDisabled}
              title={
                emailDisabled
                  ? t('approvals.reminder.emailDisabled', "L'invio email non è configurato su questo ambiente.")
                  : t('approvals.reminder.hint', 'Seleziona le persone da sollecitare')
              }
            >
              {sendingReminders ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              {selectedUserIds.size > 0
                ? t('approvals.reminder.buttonCount', 'Invia promemoria ({{count}})', { count: selectedUserIds.size })
                : t('approvals.reminder.button', 'Invia promemoria')}
            </Button>
            <Button type="button" variant="outline" size="sm" className="gap-2" onClick={handleDownloadManagementPdf} disabled={loading || downloadingPdf}>
              {downloadingPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {t('approvals.downloadPdf', 'Esporta PDF')}
            </Button>
          </div>
        </div>

        {/* Filters apply to every tab */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full md:w-[240px]">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('approvals.filters.search', 'Cerca persona…')}
              aria-label={t('approvals.filters.search', 'Cerca persona…')}
              className="h-9 pl-8"
            />
          </div>
          <ApprovalScopeToggle scope={scope} />
          <div className="hidden items-center gap-2 md:flex">{renderFilterSelects('h-9 w-[210px]')}</div>
          {hasActiveFilters && (
            <Button type="button" variant="ghost" size="sm" className="h-9 gap-1.5 text-muted-foreground" onClick={resetFilters}>
              <X className="h-4 w-4" />
              {t('approvals.filters.reset', 'Azzera filtri')}
            </Button>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={(value) => updateParams({ tab: value === DEFAULT_TAB ? null : value })}>
          <TabsList className="scrollbar-hide h-auto w-full justify-start gap-0 overflow-x-auto rounded-none border-b bg-transparent p-0 sm:gap-1">
            {RELATIONSHIP_TABS.map((type) => {
              const list = rowsByType[type];
              const pending = list.reduce((sum, row) => sum + row.pendingIds.length, 0);
              const withIssues = list.filter((row) => row.issues.length > 0).length;
              return (
                <TabsTrigger
                  key={type}
                  value={type}
                  className="-mb-px shrink-0 flex-col items-start gap-0.5 rounded-none border-b-2 border-transparent px-1.5 pb-2.5 pt-2 text-left text-muted-foreground data-[state=active]:border-orange-500 data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none sm:px-3"
                >
                  <span className="text-sm font-medium leading-tight">{t(`approvals.tabs.${type}`, type)}</span>
                  {/* Phone: just the numbers, so four tabs fit without scrolling */}
                  <span className="flex items-center gap-2 text-xs font-normal tabular-nums sm:hidden">
                    <span>{loading ? '…' : list.length}</span>
                    {!loading && pending > 0 && (
                      <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400">
                        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                        {pending}
                      </span>
                    )}
                    {!loading && withIssues > 0 && (
                      <span className="inline-flex items-center gap-1 text-red-700 dark:text-red-400">
                        <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                        {withIssues}
                      </span>
                    )}
                  </span>
                  <span className="hidden items-center gap-2 text-xs font-normal tabular-nums sm:flex">
                    <span>{loading ? '…' : peopleLabel(list.length)}</span>
                    {!loading && pending > 0 && (
                      <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400">
                        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                        {t('approvals.tabPending', '{{count}} da approvare', { count: pending })}
                      </span>
                    )}
                    {!loading && withIssues > 0 && (
                      <span className="inline-flex items-center gap-1 text-red-700 dark:text-red-400">
                        <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                        {t('approvals.issues.tabCount', '{{count}} con problemi', { count: withIssues })}
                      </span>
                    )}
                  </span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {RELATIONSHIP_TABS.map((type) => (
            <TabsContent key={type} value={type} className="mt-4">
              {renderTable(type)}
            </TabsContent>
          ))}
        </Tabs>

        {/* Selection bar: stays in view while the list scrolls */}
        {selectedUserIds.size > 0 && (
          <div className="sticky bottom-4 z-20 flex flex-wrap items-center gap-2 rounded-lg border bg-card px-3 py-2.5 shadow-lg">
            <span className="text-sm font-medium tabular-nums">
              {selectedPendingIds.length > 0
                ? t('approvals.selection.summary', '{{people}} · {{entries}} da approvare', {
                    people: peopleLabel(selectedUserIds.size),
                    entries: entriesLabel(selectedPendingIds.length),
                  })
                : t('approvals.selection.summaryPeople', '{{people}} selezionate', { people: peopleLabel(selectedUserIds.size) })}
            </span>
            <Button type="button" variant="ghost" size="sm" className="h-8 text-muted-foreground" onClick={() => setSelectedUserIds(new Set())}>
              {t('approvals.selection.clear', 'Annulla selezione')}
            </Button>
            <div className="flex-1" />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5 text-destructive hover:text-destructive"
              disabled={processing || selectedPendingIds.length === 0}
              onClick={() => beginReject(selectedPendingIds)}
            >
              <X className="h-4 w-4" />
              {t('approvals.reject', 'Rifiuta')}
            </Button>
            <Button
              type="button"
              size="sm"
              className="gap-1.5 bg-green-600 text-white hover:bg-green-700"
              disabled={processing || selectedPendingIds.length === 0}
              onClick={() => beginApprove(selectedPendingIds)}
            >
              {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {t('approvals.approveCount', 'Approva {{entries}}', { entries: entriesLabel(selectedPendingIds.length) })}
            </Button>
          </div>
        )}

        {/* Month detail */}
        <TimesheetMonthDialog
          row={detailRow}
          open={detailRow !== null}
          onOpenChange={(open) => !open && setDetailUserId(null)}
          year={year}
          month={month}
          relationshipLabel={detailRow ? relationshipLabel(detailRow.relationshipType) : ''}
          companyName={detailRow ? companyLabelFor(detailRow) : undefined}
          calendar={calendar}
          processing={processing}
          onApprove={beginApprove}
          onReject={beginReject}
          onOpenFullTimesheet={handleOpenFullTimesheet}
        />

        {/* Mobile filters */}
        <Dialog open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t('approvals.filters.open', 'Filtri')}</DialogTitle>
              <DialogDescription>{t('approvals.filters.mobileDescription', 'I filtri vengono applicati automaticamente.')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">{renderFilterSelects('w-full')}</div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setMobileFiltersOpen(false)}>
                {t('common.close', 'Chiudi')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {decisionDialogs}
      </div>
    </PageTransition>
  );
};

export default ApprovalsManager;
